let dialogTitleSeq = 0

export default {
  data() {
    return {
      modalEscHandler: null,
      modalKeydownHandler: null,
      modalWheelHandler: null,
      modalTouchMoveHandler: null,
      modalPreviouslyFocusedElement: null,
      modalPreviousBodyOverflow: null,
      modalInertedNodes: [],
    }
  },
  mounted() {
    // Note: When a modal is teleported, `this.$el` can be a placeholder comment.
    // Always prefer `$refs.modalWrapper`, and resolve it dynamically in handlers.
    const getWrapper = () =>
      (this.$refs && this.$refs.modalWrapper) || this.$el?.querySelector?.('.modalWrapper') || null

    const ensureWrapperFocusable = () => {
      const wrapper = getWrapper()
      if (wrapper && !wrapper.hasAttribute?.('tabindex')) {
        wrapper.setAttribute('tabindex', '-1')
      }
      return wrapper
    }

    // Ensure we have something we can focus if needed
    ensureWrapperFocusable()

    // Most modals declared no dialog semantics, so a screen reader never announced one
    // opening. Give each role="dialog", aria-modal and a name from its first heading,
    // unless it (or its container) already declares its own.
    const ensureDialogSemantics = () => {
      const wrapper = getWrapper()
      if (!wrapper || wrapper.hasAttribute('role')) return
      if (wrapper.parentElement?.matches('[role="dialog"], [role="alertdialog"]')) return
      const heading = wrapper.querySelector('h1, h2, h3, h4')
      if (!heading) return
      heading.id = heading.id || `modal-title-${++dialogTitleSeq}`
      wrapper.setAttribute('role', 'dialog')
      wrapper.setAttribute('aria-modal', 'true')
      wrapper.setAttribute('aria-labelledby', heading.id)
    }
    ensureDialogSemantics()

    // aria-modal alone leaves everything behind the dialog focusable. Walk up from the
    // wrapper to <body> and inert each sibling on the way, which works whether the modal
    // is teleported to body or rendered inside #app. Only an aria-live node itself is
    // skipped (the toaster, teleported to body in App.vue): inert is inherited and cannot
    // be undone on a descendant, and toasts fire from dialogs ("... added to cart").
    const setBackgroundInert = () => {
      const wrapper = getWrapper()
      if (!wrapper) return
      let node = wrapper
      while (node && node !== document.body && node.parentElement) {
        Array.from(node.parentElement.children).forEach((sibling) => {
          // Already inert: an outer modal owns it and it must stay inert when this one closes.
          if (sibling === node || sibling.hasAttribute('inert') || sibling.hasAttribute('aria-live')) return
          sibling.setAttribute('inert', '')
          this.modalInertedNodes.push(sibling)
        })
        node = node.parentElement
      }
    }

    this.modalPreviouslyFocusedElement = document.activeElement
    this.modalPreviousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const isTopmostModal = () => {
      const wrapper = getWrapper()
      if (!wrapper) return false
      const wrappers = document.querySelectorAll('.modalWrapper')
      if (!wrappers.length) return false
      return wrappers[wrappers.length - 1] === wrapper
    }

    // Escape to close (only topmost modal)
    this.modalEscHandler = (e) => {
      if (e.key !== 'Escape') return
      if (typeof this.closeModal !== 'function') return

      const wrapper = getWrapper()
      const active = document.activeElement
      const focusInside = !!(wrapper && active && wrapper.contains(active))
      if (!focusInside && !isTopmostModal()) return

      this.closeModal()
    }

    // Trap tab inside modal
    const getFocusable = () => {
      const wrapper = getWrapper()
      if (!wrapper || !wrapper.querySelectorAll) return []
      const nodes = wrapper.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      )
      return Array.from(nodes).filter((el) => {
        // Skip hidden elements
        if (el.offsetParent === null && el !== document.activeElement) return false
        return true
      })
    }

    this.modalKeydownHandler = (e) => {
      if (e.key !== 'Tab') return
      const wrapper = getWrapper()
      if (!wrapper) return
      if (!wrapper.contains(document.activeElement)) return

      const focusable = getFocusable()
      if (focusable.length === 0) {
        e.preventDefault()
        wrapper.focus?.()
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault()
          last.focus()
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }

    // Prevent background scroll while modal open.
    // Allow all wheel/touch scrolling that starts inside the modal wrapper. Background is locked via body overflow.
    const shouldAllowScroll = (target) => {
      // If wheel/touch originates inside ANY open modal wrapper, allow it.
      // This prevents underlying modals' listeners from blocking the active/top modal.
      if (target && target.closest && target.closest('.modalWrapper')) return true
      const wrapper = getWrapper()
      if (!wrapper) return true
      return wrapper.contains(target)
    }

    this.modalWheelHandler = (e) => {
      if (shouldAllowScroll(e.target)) return
      e.preventDefault()
    }

    this.modalTouchMoveHandler = (e) => {
      if (shouldAllowScroll(e.target)) return
      e.preventDefault()
    }

    document.addEventListener('keydown', this.modalEscHandler)
    document.addEventListener('keydown', this.modalKeydownHandler, true)
    window.addEventListener('wheel', this.modalWheelHandler, { passive: false, capture: true })
    window.addEventListener('touchmove', this.modalTouchMoveHandler, { passive: false, capture: true })

    // Initial focus: if nothing inside modal has focus, bring focus in.
    this.$nextTick(() => {
      const wrapper = ensureWrapperFocusable()
      if (!wrapper) return
      setBackgroundInert()
      const active = document.activeElement
      if (active && wrapper.contains(active)) return

      const focusable = getFocusable()
      if (focusable.length > 0) {
        focusable[0].focus()
      } else {
        wrapper.focus?.()
      }
    })
  },
  beforeUnmount() {
    // Before restoring focus below: the trigger is one of the inerted nodes, and focus()
    // does nothing on an inert element.
    this.modalInertedNodes.forEach((node) => node.removeAttribute('inert'))
    this.modalInertedNodes = []
    if (this.modalEscHandler) {
      document.removeEventListener('keydown', this.modalEscHandler)
    }
    if (this.modalKeydownHandler) {
      document.removeEventListener('keydown', this.modalKeydownHandler, true)
    }
    if (this.modalWheelHandler) {
      window.removeEventListener('wheel', this.modalWheelHandler, { capture: true })
    }
    if (this.modalTouchMoveHandler) {
      window.removeEventListener('touchmove', this.modalTouchMoveHandler, { capture: true })
    }
    if (this.modalPreviousBodyOverflow !== null) {
      document.body.style.overflow = this.modalPreviousBodyOverflow
    }
    const el = this.modalPreviouslyFocusedElement
    if (el && typeof el.focus === 'function') {
      try {
        el.focus({ preventScroll: true })
      } catch (_) {
        el.focus()
      }
    }
  },
}
